import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useAccessToken } from "./useAuthentication";
import { backendUrl } from "../constants";
import { authedFetch } from "../lib/authedFetch";
import { anyMatches } from "../lib/accessPatterns";

// What `/me` (flask-back-end/resources/access/views.py) returns.
export interface MePayload {
  email: string;
  groups: string[];
  features: string[];
  reps: string[];
  funds: string[];
  all_reps: boolean;
  all_funds: boolean;
  super_admin: boolean;
  /** off | shadow | enforce — the mode that actually applies right now. */
  mode: "off" | "shadow" | "enforce";
  configured_mode: string;
  configured: boolean;
  local: boolean;
}

/** "Preview as" — a super admin looking at the nav the way another group sees it.
 *  Frontend-only: the backend keeps enforcing the admin's REAL access. */
export interface AccessPreview {
  label: string;
  features: string[];
  /** Who is being previewed. Pages whose DATA depends on grants (Fund View's
   *  fund list) send it to the server, which honours it only for an access
   *  admin; everything else stays nav-only. Absent on older stored previews. */
  subject?: { type: "group" | "user"; key: string };
}

export interface AccessValue {
  loading: boolean;
  error: string | null;
  me: MePayload | null;
  /** The effective mode for RENDERING: a preview behaves like enforce. */
  mode: "off" | "shadow" | "enforce";
  preview: AccessPreview | null;
  setPreview: (p: AccessPreview | null) => void;
  /** Is this feature granted? True while loading / off / on error. */
  can: (feature: string) => boolean;
  /** Should this feature be DRAWN? Hidden only in enforce mode. */
  visible: (feature: string) => boolean;
  /** Not granted, in a mode where that matters — shadow shows a lock. */
  locked: (feature: string) => boolean;
  refresh: () => void;
}

const AccessContext = createContext<AccessValue | null>(null);

const PREVIEW_KEY = "ifm.access.preview";
const RETRY_DELAYS_MS = [1000, 3000, 8000];

const readPreview = (): AccessPreview | null => {
  try {
    const raw = sessionStorage.getItem(PREVIEW_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as AccessPreview;
    return p && Array.isArray(p.features) ? p : null;
  } catch {
    return null;
  }
};

/**
 * Resolves the signed-in person's access ONCE for the whole app and shares it.
 * Mounted in App.tsx under AccessTokenProvider (it needs `authReady`).
 *
 * Failure posture: a `/me` that cannot be reached NEVER hides the app. The nav
 * renders as if everything were allowed and the backend keeps gating every
 * request — the Checkpoint pill once failed CLOSED on a transient error and
 * stranded entitled users on an Access Denied page; this deliberately does
 * the opposite. It retries three times with backoff and then shows a quiet
 * banner (RootLayout) rather than a wall.
 */
export const AccessProvider = ({ children }: { children: ReactNode }) => {
  const { authReady, accounts } = useAccessToken();
  const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === "true";

  const [me, setMe] = useState<MePayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreviewState] = useState<AccessPreview | null>(readPreview);
  // The ONE refusal that is an answer rather than a failure: the backend knows
  // this person is not in Users & Groups (config/auth.py, code not_a_member).
  // Everything else keeps the fail-open posture described above.
  const [notMember, setNotMember] = useState<boolean>(false);
  const attempt = useRef(0);
  const timer = useRef<number | undefined>(undefined);

  const load = useCallback(async () => {
    window.clearTimeout(timer.current);
    try {
      const res = await authedFetch(`${backendUrl}/me`);
      if (res.status === 403) {
        const body = (await res.clone().json().catch(() => null)) as { code?: string } | null;
        if (body?.code === "not_a_member") {
          setNotMember(true);
          setLoading(false);
          return;
        }
      }
      if (!res.ok) throw new Error(`/me answered ${res.status}`);
      setNotMember(false);
      const body = (await res.json()) as MePayload;
      setMe(body);
      setError(null);
      setLoading(false);
      attempt.current = 0;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      const delay = RETRY_DELAYS_MS[attempt.current];
      if (delay !== undefined) {
        attempt.current += 1;
        timer.current = window.setTimeout(() => { void load(); }, delay);
      } else {
        setError(msg);
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Nothing to resolve until the bearer exists (or auth is bypassed locally).
    if (!bypassAuth && (!authReady || accounts.length === 0)) return;
    attempt.current = 0;
    setLoading(true);
    void load();
    return () => window.clearTimeout(timer.current);
  }, [authReady, accounts.length, bypassAuth, load]);

  const setPreview = useCallback((p: AccessPreview | null) => {
    setPreviewState(p);
    try {
      if (p) sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(p));
      else sessionStorage.removeItem(PREVIEW_KEY);
    } catch {
      /* storage unavailable — preview is still applied in memory */
    }
  }, []);

  const value = useMemo<AccessValue>(() => {
    const realMode: MePayload["mode"] = me?.mode ?? "off";
    const mode: MePayload["mode"] = preview ? "enforce" : realMode;
    const patterns: string[] | null = preview ? preview.features : me ? me.features : null;
    const decided = !loading && !error && patterns !== null && mode !== "off";
    const can = (feature: string): boolean => {
      if (!decided) return true;
      return anyMatches(patterns as string[], feature);
    };
    return {
      loading,
      error,
      me,
      mode,
      preview,
      setPreview,
      can,
      visible: (feature) => (mode === "enforce" ? can(feature) : true),
      locked: (feature) => decided && !can(feature),
      refresh: () => { attempt.current = 0; setLoading(true); void load(); },
    };
  }, [loading, error, me, preview, setPreview, load]);

  if (notMember) {
    return <NoAccess email={accounts[0]?.username ?? ""} onRetry={value.refresh} />;
  }
  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

const NoAccess = ({ email, onRetry }: { email: string; onRetry: () => void }) => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
    <div className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 text-center shadow-sm">
      <h1 className="text-lg font-semibold text-gray-900">You don't have access to IFM yet</h1>
      <p className="mt-2 text-sm text-gray-600">
        {email ? <>You're signed in as <span className="font-medium">{email}</span>, but </> : "Your account "}
        isn't set up in Users &amp; Groups. Ask an IFM admin to add you to a group.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-4 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
      >
        Try again
      </button>
    </div>
  </div>
);

export const useAccess = (): AccessValue => {
  const ctx = useContext(AccessContext);
  if (!ctx) throw new Error("useAccess must be used within <AccessProvider>");
  return ctx;
};
